import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Upload, Image, Link as LinkIcon, Tag, DollarSign, Store, 
    Loader2, ChevronDown, Check, Search, X
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { API, API_BASE } from '../config/apiBase';


export default function CreateDealPage() {
    const { user, isApproved, isAdmin } = useAuth();
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [parsing, setParsing] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        image_url: '',
        original_price: '',
        current_price: '',
        store_name: '',
        store_url: '',
        affiliate_link: '',
        category_id: '',
        coupon_code: ''
    });

    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }
        if (!isApproved && !isAdmin) {
            toast.error('Você precisa ser um usuário aprovado para publicar ofertas');
            navigate('/');
            return;
        }
        fetchCategories();
    }, [user, isApproved, isAdmin, navigate]);

    const fetchCategories = async () => {
        try {
            const response = await fetch(`${API}/categories`);
            const data = await response.json();
            setCategories(data);
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    // Parse link automatically
    const parseLink = async (url) => {
        if (!url || url.length < 10) return;
        setParsing(true);
        try {
            const response = await fetch(`${API}/parse-link`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ url })
            });
            const data = await response.json();
            
            if (data.success) {
                setFormData(prev => ({
                    ...prev,
                    title: data.title || prev.title,
                    image_url: data.image || prev.image_url,
                    current_price: data.price || prev.current_price,
                    store_url: data.final_url || url
                }));
                toast.success('Dados capturados automaticamente!');
            } else {
                toast.info('Não foi possível capturar dados. Preencha manualmente.');
            }
        } catch (error) {
            toast.info('Preencha os dados manualmente.');
        } finally {
            setParsing(false);
        }
    };

    // Upload image handler
    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
        if (!validTypes.includes(file.type)) {
            toast.error('Formato não suportado. Use JPG, PNG, WebP ou GIF.');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Arquivo muito grande. Maximo: 5MB.');
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const token = localStorage.getItem('token');
            const res = await fetch(`${API}/upload/image`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                credentials: 'include',
                body: fd
            });
            if (res.ok) {
                const data = await res.json();
                const fullUrl = `${API_BASE}${data.url}`;
                setFormData(prev => ({ ...prev, image_url: fullUrl }));
                toast.success('Imagem enviada com sucesso!');
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Erro no upload');
            }
        } catch { toast.error('Erro ao enviar imagem'); }
        finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
    };

    // Check steps completion
    const steps = [
        { id: 1, label: 'Link da oferta', done: formData.affiliate_link.length > 5 },
        { id: 2, label: 'Título da oferta', done: formData.title.length > 3 },
        { id: 3, label: 'Preço da oferta', done: formData.current_price.length > 0 }
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.title || !formData.image_url || !formData.current_price || !formData.affiliate_link || !formData.category_id) {
            toast.error('Preencha todos os campos obrigatórios');
            return;
        }
        
        setLoading(true);
        try {
            const response = await fetch(`${API}/produtos`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    ...formData,
                    original_price: parseFloat(formData.original_price) || parseFloat(formData.current_price),
                    current_price: parseFloat(formData.current_price)
                })
            });

            if (!response.ok) {
                throw new Error('Failed to create deal');
            }

            const deal = await response.json();
            toast.success(isAdmin ? 'Oferta publicada!' : 'Oferta enviada para aprovação!');
            navigate(`/oferta/${deal.deal_id}`);
        } catch (error) {
            toast.error('Erro ao criar oferta');
        } finally {
            setLoading(false);
        }
    };

    if (!user || (!isApproved && !isAdmin)) {
        return null;
    }

    return (
        <div className="min-h-screen" style={{ background: 'var(--bg)' }} data-testid="create-deal-page">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="mb-8">
                    <h1 className="text-3xl font-outfit font-bold" style={{ color: 'var(--text)' }}>Publicar Oferta</h1>
                    <p className="mt-2" style={{ color: 'var(--muted)' }}>
                        {isAdmin ? 'Sua oferta será publicada imediatamente' : 'Sua oferta será analisada antes de ser publicada'}
                    </p>
                </div>

                {/* Steps Progress */}
                <div className="mb-6 p-4 rounded-lg" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <p className="text-sm font-medium mb-3" style={{ color: 'var(--muted)' }}>Siga o passo a passo:</p>
                    <div className="flex items-center gap-4">
                        {steps.map((step, i) => (
                            <div key={step.id} className="flex items-center gap-2">
                                <div 
                                    className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
                                    style={{ 
                                        background: step.done ? 'var(--gold-2)' : 'var(--surface-2)', 
                                        color: step.done ? '#0A0A0A' : 'var(--muted)'
                                    }}
                                >
                                    {step.done ? <Check className="w-4 h-4" /> : step.id}
                                </div>
                                <span className="text-xs hidden sm:inline" style={{ color: step.done ? 'var(--gold-2)' : 'var(--muted)' }}>
                                    {step.label}
                                </span>
                                {i < steps.length - 1 && <div className="w-8 h-px" style={{ background: 'var(--border)' }} />}
                            </div>
                        ))}
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Affiliate Link - FIRST */}
                    <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--muted)' }}>
                            <LinkIcon className="w-4 h-4 inline mr-2" />
                            Link da Oferta * (Cole aqui para captura automática)
                        </label>
                        <div className="flex gap-2">
                            <input
                                type="url"
                                value={formData.affiliate_link}
                                onChange={(e) => setFormData({ ...formData, affiliate_link: e.target.value })}
                                onBlur={(e) => parseLink(e.target.value)}
                                placeholder="https://loja.com.br/produto ou link de afiliado"
                                className="flex-1 rounded-lg px-4 py-3 text-sm"
                                style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }}
                                required
                                data-testid="deal-link-input"
                            />
                            <Button 
                                type="button" 
                                onClick={() => parseLink(formData.affiliate_link)}
                                disabled={parsing}
                                className="btn-gold px-4"
                            >
                                {parsing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                            </Button>
                        </div>
                        {parsing && <p className="text-xs mt-1" style={{ color: 'var(--gold-2)' }}>Buscando dados...</p>}
                    </div>

                    {/* Title */}
                    <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--muted)' }}>
                            Título da Oferta *
                        </label>
                        <input
                            type="text"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            placeholder="Ex: iPhone 15 Pro 256GB com 20% de desconto"
                            className="w-full rounded-lg px-4 py-3 text-sm"
                            style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }}
                            required
                            data-testid="deal-title-input"
                        />
                    </div>

                    {/* Image: Upload or URL */}
                    <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--muted)' }}>
                            <Image className="w-4 h-4 inline mr-2" />
                            Imagem do Produto *
                        </label>
                        {/* Upload area */}
                        <div className="mb-2 p-4 rounded-lg text-center cursor-pointer transition-colors"
                            style={{ background: 'var(--surface)', border: '2px dashed var(--border)' }}
                            onClick={() => fileInputRef.current?.click()}
                            data-testid="deal-image-upload-area"
                        >
                            <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif"
                                onChange={handleImageUpload} className="hidden" data-testid="deal-image-file-input" />
                            {uploading ? (
                                <div className="flex items-center justify-center gap-2">
                                    <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--sv-gold-primary)' }} />
                                    <span className="text-sm" style={{ color: 'var(--muted)' }}>Enviando...</span>
                                </div>
                            ) : (
                                <div>
                                    <Upload className="w-6 h-6 mx-auto mb-1" style={{ color: 'var(--muted-2)' }} />
                                    <p className="text-xs" style={{ color: 'var(--muted)' }}>Clique para enviar imagem (JPG, PNG, WebP - max 5MB)</p>
                                </div>
                            )}
                        </div>
                        {/* Or paste URL */}
                        <div className="flex items-center gap-2 mb-2">
                            <div className="h-px flex-1" style={{ background: 'var(--border)' }} />
                            <span className="text-xs" style={{ color: 'var(--muted-2)' }}>ou cole uma URL</span>
                            <div className="h-px flex-1" style={{ background: 'var(--border)' }} />
                        </div>
                        <input
                            type="url"
                            value={formData.image_url}
                            onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                            placeholder="https://exemplo.com/imagem.jpg"
                            className="w-full rounded-lg px-4 py-3 text-sm"
                            style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }}
                            data-testid="deal-image-input"
                        />
                        {formData.image_url && (
                            <div className="mt-3 rounded-lg p-4 inline-block relative" style={{ background: 'var(--surface)' }}>
                                <img 
                                    src={formData.image_url} 
                                    alt="Preview" 
                                    className="max-h-32 object-contain"
                                    onError={(e) => e.target.style.display = 'none'}
                                />
                                <button type="button" className="absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center"
                                    style={{ background: 'rgba(0,0,0,0.6)' }}
                                    onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))}
                                    data-testid="deal-image-clear">
                                    <X className="w-3 h-3" style={{ color: '#fff' }} />
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Prices */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--muted)' }}>
                                <DollarSign className="w-4 h-4 inline mr-2" />
                                Preço Original
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                value={formData.original_price}
                                onChange={(e) => setFormData({ ...formData, original_price: e.target.value })}
                                placeholder="999.00"
                                className="w-full rounded-lg px-4 py-3 text-sm"
                                style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }}
                                data-testid="deal-original-price-input"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--muted)' }}>
                                Preço Atual *
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                value={formData.current_price}
                                onChange={(e) => setFormData({ ...formData, current_price: e.target.value })}
                                placeholder="799.00"
                                className="w-full rounded-lg px-4 py-3 text-sm"
                                style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }}
                                required
                                data-testid="deal-current-price-input"
                            />
                        </div>
                    </div>

                    {/* Store Info */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--muted)' }}>
                                <Store className="w-4 h-4 inline mr-2" />
                                Nome da Loja *
                            </label>
                            <input
                                type="text"
                                value={formData.store_name}
                                onChange={(e) => setFormData({ ...formData, store_name: e.target.value })}
                                placeholder="Amazon, Magazine Luiza..."
                                className="w-full rounded-lg px-4 py-3 text-sm"
                                style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }}
                                required
                                data-testid="deal-store-name-input"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--muted)' }}>
                                URL da Loja
                            </label>
                            <input
                                type="url"
                                value={formData.store_url}
                                onChange={(e) => setFormData({ ...formData, store_url: e.target.value })}
                                placeholder="https://loja.com.br"
                                className="w-full rounded-lg px-4 py-3 text-sm"
                                style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }}
                                data-testid="deal-store-url-input"
                            />
                        </div>
                    </div>

                    {/* Affiliate Link */}
                    <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--muted)' }}>
                            <LinkIcon className="w-4 h-4 inline mr-2" />
                            Link de Afiliado / Link da Oferta *
                        </label>
                        <input
                            type="url"
                            value={formData.affiliate_link}
                            onChange={(e) => setFormData({ ...formData, affiliate_link: e.target.value })}
                            placeholder="https://loja.com.br/produto?ref=supervitrine"
                            className="w-full rounded-lg px-4 py-3 text-sm"
                            style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }}
                            required
                            data-testid="deal-affiliate-link-input"
                        />
                    </div>

                    {/* Category */}
                    <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--muted)' }}>
                            <Tag className="w-4 h-4 inline mr-2" />
                            Categoria *
                        </label>
                        <Select
                            value={formData.category_id}
                            onValueChange={(value) => setFormData({ ...formData, category_id: value })}
                        >
                            <SelectTrigger className="w-full" style={{ background: 'var(--surface)', borderColor: 'var(--border)', color: 'var(--text)' }} data-testid="deal-category-select">
                                <SelectValue placeholder="Selecione uma categoria" />
                            </SelectTrigger>
                            <SelectContent style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
                                {categories.map((cat) => (
                                    <SelectItem 
                                        key={cat.category_id} 
                                        value={cat.category_id}
                                        style={{ color: 'var(--text)' }}
                                    >
                                        {cat.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Coupon */}
                    <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--muted)' }}>
                            Cupom de Desconto (opcional)
                        </label>
                        <input
                            type="text"
                            value={formData.coupon_code}
                            onChange={(e) => setFormData({ ...formData, coupon_code: e.target.value.toUpperCase() })}
                            placeholder="DESCONTO10"
                            className="w-full rounded-lg px-4 py-3 text-sm font-mono"
                            style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }}
                            data-testid="deal-coupon-input"
                        />
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--muted)' }}>
                            Descrição (opcional)
                        </label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            placeholder="Descreva os detalhes da oferta..."
                            rows={4}
                            className="w-full rounded-lg px-4 py-3 text-sm resize-none"
                            style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }}
                            data-testid="deal-description-input"
                        />
                    </div>

                    {/* Submit */}
                    <Button
                        type="submit"
                        disabled={loading}
                        className="w-full btn-gold py-4 text-base"
                        data-testid="deal-submit-btn"
                    >
                        {loading ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                            <>
                                <Upload className="w-5 h-5 mr-2" />
                                {isAdmin ? 'Publicar Oferta' : 'Enviar para Aprovação'}
                            </>
                        )}
                    </Button>
                </form>
            </div>
        </div>
    );
}
